import { useEffect, useMemo } from 'react'
import PropTypes from 'prop-types'
import ImmutablePropTypes from 'react-immutable-proptypes'
import { Map, List } from 'immutable'
import { connect as connectRedux } from 'react-redux'
import _get from 'lodash/get'
import { getBoundActions } from '../../actions/index'
import {
  EN,
  DE,
  FR,
  ES,
} from '../../services/languages/constants'
import { US } from '../../services/country/index'
import { get as getConfig } from '../../config/index'
import { getUserLanguagePrimary } from '../../services/user/index'
import { MODAL_TYPES } from '../Modal/index'
import { TYPE_ANNOUNCEMENT_MODAL } from '../../services/dialog/index'
import { RESOLVER_TYPE_NOT_FOUND } from '../../services/resolver/types'
import {
  isAccountPage,
  isSharePage,
  isPortal,
  isCart,
  isCartAccessDenied,
  isPlans,
  isGift,
  isGo,
  isGetStarted,
  isFullPlayer,
} from '../../services/url/index'
import { isEntitled } from '../../services/subscription/index'

const config = getConfig()
const mediaId = _get(config, ['features', 'announcementModal', 'mediaId'])

const ANNOUNCEMENT_ACTIVATION_TIME = 2000

function getStaticText (language) {
  const staticText = {
    [EN]: {
      heading: 'Exclusive interview with Gaia Founder, Jirka Rysavy',
      description: 'Discover How Solar-Based Quantum Technology is Transforming Human Health and How You Can Be Among the First to Access It.',
      button: 'Learn More',
    },
    [DE]: {
      heading: '',
      description: '',
      button: null,
    },
    [FR]: {
      heading: '',
      description: '',
      button: null,
    },
    [ES]: {
      heading: '',
      description: '',
      button: null,
    },
  }
  const localizedText = staticText[language] || staticText[EN]
  return Map(localizedText)
}

function Announcement (props) {
  const {
    isTourActive,
    shouldHideAnnouncement,
    setFeatureTrackingDataPersistent,
    auth,
    language,
    renderModal,
    dismissModal,
    interstitialVisible,
    resolver,
    announcementSeenCount,
  } = props

  const staticText = getStaticText(language)
  const isUserEntitled = isEntitled(auth.get('subscriptions', List()))

  const shouldShowAnnouncement = useMemo(() => {
    const path = resolver.getIn(['data', 'path'])
    const query = resolver.getIn(['data', 'query'])

    /**
     * do not show announcement if language is not English
     */
    if (language !== EN) {
      return false
    }
    /**
     * do not show announcement if user already seen it
     * or tour is active or interstitial is active
     * or not entitled
     */
    if (shouldHideAnnouncement || isTourActive || interstitialVisible || !isUserEntitled) {
      return false
    }
    /**
     * do not show announcemnt if the page is not found
     */
    if (resolver.getIn(['data', 'type']) === RESOLVER_TYPE_NOT_FOUND) {
      return false
    }
    /**
     * do not show announcemnt for the specific pages
     */
    if (
      isAccountPage(path)
      || isSharePage(path)
      || isPortal(path)
      || isCart(path)
      || isCartAccessDenied(path)
      || isPlans(path)
      || isGift(path)
      || isGo(path)
      || isGetStarted(path)
      || isFullPlayer(query)
    ) {
      return false
    }
    return true
  }, [
    resolver,
    shouldHideAnnouncement,
    isTourActive,
    interstitialVisible,
    isUserEntitled,
    language,
  ])

  const handleCloseClick = () => {
    handleModalDismiss()
    dismissModal(TYPE_ANNOUNCEMENT_MODAL)
  }

  const handleModalDismiss = () => {
    setFeatureTrackingDataPersistent({
      data: Map({ ignitonIntroModal: announcementSeenCount + 1 }),
      auth,
    })
  }

  useEffect(() => {
    const timer = setTimeout(() => {
      if (shouldShowAnnouncement) {
        renderModal(TYPE_ANNOUNCEMENT_MODAL, {
          type: MODAL_TYPES.ROYAL_VIDEO,
          onDismiss: handleModalDismiss,
          onCloseClick: handleCloseClick,
          staticText,
          buttonUrl: 'https://www.gaia.com/igniton?utm_campaign=webapp-video-modal',
          mediaId,
        })
      }
    }, ANNOUNCEMENT_ACTIVATION_TIME)
    return () => {
      clearTimeout(timer)
    }
  }, [shouldShowAnnouncement])

  return null
}

Announcement.propTypes = {
  shouldHideAnnouncement: PropTypes.bool,
  isTourActive: PropTypes.bool,
  language: PropTypes.string.isRequired,
  auth: ImmutablePropTypes.map.isRequired,
  interstitialVisible: PropTypes.bool,
  checkoutComplete: PropTypes.bool,
  announcementSeenCount: PropTypes.number,
}

export default connectRedux(
  (state) => {
    const { user, auth } = state
    const userCountry = auth.get('country') || user.getIn(['data', 'requestCountry'])

    return {
      shouldHideAnnouncement: userCountry !== US || state.featureTracking.getIn(['data', 'ignitonIntroModal'], 0) >= 1,
      announcementSeenCount: state.featureTracking.getIn(['data', 'ignitonIntroModal'], 0),
      isTourActive: state.tour.get('isActive'),
      language: getUserLanguagePrimary(state.user) || EN,
      auth: state.auth,
      interstitialVisible: state.interstitial.get('view'),
      resolver: state.resolver,
    }
  },
  (dispatch) => {
    const actions = getBoundActions(dispatch)
    return {
      setFeatureTrackingDataPersistent: actions.featureTracking.setFeatureTrackingDataPersistent,
      renderModal: actions.dialog.renderModal,
      dismissModal: actions.dialog.dismissModal,
    }
  },
)(Announcement)
